/**
 * How the import reads a BOOLEAN cell from a guest or contact spreadsheet —
 * the mail opt-in column above all, and the BOOLEAN custom fields the wizard
 * maps.
 *
 * ⚑14 (Welle Z, 15.10.): the importer knew only true/false/yes/no/1/0/y/n.
 * A German sheet saying "nein" in its opt-in column got the warning "kein
 * gültiger Boolean-Wert", the cell became empty, the guest landed with
 * `emailOptIn: null` (unknown consent) — and the mail went out to someone
 * whose sheet said NO. The words now come from ONE data file,
 * `import-boolean-words.json`: today's values, the Yes/No of every cockpit UI
 * language (as the cockpit itself prints them, `boolean.true` /
 * `boolean.false`, and as people type them), and what a localised Excel writes
 * for a boolean cell (WAHR/FALSCH, VRAI/FAUX, …).
 *
 * The doors, all through {@link resolveImportBooleanCell} or the two readers
 * below:
 *  - the wizard (guest and contact import): the warning triangle
 *    (`getInvalidDetails`), the error list (`collectImportErrors`), the
 *    check-step preview (`previewImportCell`), the "values dropped" notice
 *    (`countDroppedImportValues`) and the row builder (`parseGuest`);
 *  - Velo's spreadsheet import: the mail opt-in only — Velo maps every custom
 *    column as STRING and stores its raw text (`AiImportTools` resolveField).
 *
 * A word that means YES in one language and NO in another ("jo": lb yes, sq
 * no) is in neither list. The mail opt-in reads it as NO — no consent is the
 * safe side (audit-14 M-1); every other BOOLEAN column leaves it unknown (a
 * warning, then the column default). A word that a future edit put in both
 * lists is treated exactly like a listed ambiguous word.
 */
import data from "./import-boolean-words.json";

type WordPair = { readonly true: readonly string[]; readonly false: readonly string[] };

export type ImportBooleanWordData = {
  readonly symbols: WordPair;
  readonly languages: Readonly<Record<string, WordPair>>;
  /** Excel's localised TRUE()/FALSE(), per UI language Excel localises. */
  readonly spreadsheet: Readonly<Record<string, WordPair>>;
  readonly excludedAmbiguous: readonly {
    readonly word: string;
    readonly true: readonly string[];
    readonly false: readonly string[];
    readonly why: string;
  }[];
};

export const IMPORT_BOOLEAN_WORDS: ImportBooleanWordData = data;

const TRUE_WORDS = new Set<string>();
const FALSE_WORDS = new Set<string>();
for (const pair of [
  data.symbols,
  ...Object.values(data.languages),
  ...Object.values(data.spreadsheet),
] as WordPair[]) {
  for (const w of pair.true) TRUE_WORDS.add(w);
  for (const w of pair.false) FALSE_WORDS.add(w);
}
const AMBIGUOUS_WORDS = new Set<string>(data.excludedAmbiguous.map((e) => e.word));

/**
 * Whitespace and the invisible characters a paste from Excel/Word carries
 * (zero-width space/joiners, word joiner, BOM). JS `\s` already covers NBSP,
 * U+2000–200A, U+3000 and U+FEFF; the rest is named.
 */
const SPACE_OR_INVISIBLE = /[\s\u200b-\u200d\u2060\ufeff]+/g;

/**
 * The form a cell is compared in: invisible characters and whitespace runs
 * become one space, the edges are trimmed, lower case, NFC (a "Não" typed with
 * a combining tilde is the same word).
 */
export function normalizeImportBooleanWord(value: string): string {
  return value
    .normalize("NFC")
    .replace(SPACE_OR_INVISIBLE, " ")
    .trim()
    .toLowerCase()
    .normalize("NFC");
}

type Reading = "yes" | "no" | "ambiguous" | null;

function read(value: unknown): Reading {
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value !== "string") return null;
  const word = normalizeImportBooleanWord(value);
  const yes = TRUE_WORDS.has(word);
  const no = FALSE_WORDS.has(word);
  if ((yes && no) || AMBIGUOUS_WORDS.has(word)) return "ambiguous";
  return yes ? "yes" : no ? "no" : null;
}

/**
 * true / false for a recognised word, null for anything else (an empty cell,
 * an unknown or ambiguous word, a non-string). A real boolean passes through.
 */
export function parseImportBoolean(value: unknown): boolean | null {
  const r = read(value);
  return r === "yes" ? true : r === "no" ? false : null;
}

/**
 * The mail opt-in's reading: like {@link parseImportBoolean}, but an
 * ambiguous word is a NO — never a YES, never unknown (audit-14 M-1).
 */
export function parseImportOptIn(value: unknown): boolean | null {
  const r = read(value);
  return r === "yes" ? true : r === "no" || r === "ambiguous" ? false : null;
}

/**
 * The guest's built-in consent opt-ins — the mail opt-in and (SMS channel
 * wave) the SMS opt-in — not a custom field of the same name. Both are read
 * the consent way: an ambiguous word is a NO.
 */
export function isMailOptInField(
  field: { name?: string; additionalField?: boolean } | null | undefined,
): boolean {
  return (field?.name === "emailOptIn" || field?.name === "smsOptIn") && !field.additionalField;
}

/**
 * The guest's built-in SMS opt-in — not a custom field of the same name. Its
 * cells are read even more strictly than the mail opt-in's (audit r1 C8): an
 * unknown word never takes the column default.
 */
export function isSmsOptInField(
  field: { name?: string; additionalField?: boolean } | null | undefined,
): boolean {
  return field?.name === "smsOptIn" && !field.additionalField;
}

/**
 * What one BOOLEAN cell imports as — the ONE rule the row builder, the
 * check-step preview, the warning and the "values dropped" notice share
 * (audit-14 H-1):
 *  1. the cell, when it is a Yes/No (for the mail opt-in an ambiguous word
 *     is a NO);
 *  2. else — empty, or not a Yes/No — the column default the organiser set
 *     in the mapper ("true"/"false"), read the same way;
 *  3. else null: unset.
 * `onlyEmptyTakesDefault` (the SMS opt-in, audit r1 C8): step 2 only for an
 * EMPTY cell — a word that is neither yes nor no ("vielleicht", "k.A.") stays
 * unset instead of becoming the column's "Yes", a consent nobody gave.
 */
export function resolveImportBooleanCell(
  cell: unknown,
  columnDefault: unknown,
  optIn: boolean,
  onlyEmptyTakesDefault = false,
): boolean | null {
  const reader = optIn ? parseImportOptIn : parseImportBoolean;
  const fromCell = reader(cell);
  if (fromCell !== null) return fromCell;
  const empty = cell === null || cell === undefined || String(cell).trim() === "";
  if (onlyEmptyTakesDefault && !empty) return null;
  return reader(columnDefault);
}
